import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Accordion, Card as BSCard, Dropdown } from 'react-bootstrap';
import { AccordionHeader } from 'components/DesignSystems/Accordion/AccordionHeader';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AddLayerFolderModal } from './AddLayerFolderModal';
import { useUscgMapContext } from './UscgMapContext';
import { LayerListItem } from './LayerListItem';
import { LayerDetailModal } from './LayerDetailModal';
import { ArcGisConnectButton } from './ArcGisConnectButton';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type {
  InternalLayer,
  LayerWithNodeMeta,
} from 'hooks/internalLayer/types';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useIsCommandCenter } from './utils/isCommandCenter';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { ViewLayerFolderModal } from './ViewLayerFolderModal';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import { useDeleteLayerNodeMutation } from 'hooks/layerNode/useDeleteLayerNodeMutation';
import type { LayerGroupDto, LayerFolderItemDto } from 'hooks/layerNode/types';
import { OnAddLayer } from './utils/constants';
import { MapLayersBackButton } from './MapLayersBackButton';
import {
  useHubChromeEnabled,
  usePowerOutagesFlag,
  useTennesseeNationalGuardFlag,
} from 'utils/featureFlags';
import { registerHubCopTourApi } from '../../hubCopTour/hubCopTourBridge';
import { registerFocCopLayersTourApi } from '../../focCopLayersTour/focCopLayersTourBridge';
import { registerFocFsltpTourApi } from '../../focFsltpTour/focFsltpTourBridge';
import { TakLayerItem } from './TakLayerItem';
import { PowerOutageLayerItem } from './PowerOutageLayerItem';
import { EriLayerItem } from './EriLayerItem';
import { IncidentAreaLayerItem } from './IncidentAreaLayerItem';
import {
  SitStatEriLayerItem,
  SitStatLayerFolderRow,
  SitStatPowerOutageLayerItem,
  SitStatTakLayerItem,
} from '../SitStatTab/SitStatLayerItems';
import { SharedTnGuardLayerTimeControl } from './SharedTnGuardLayerTimeControl';
import { getExternalLayerTimeConfig } from './arcgis-integration/tennesseeNationalGuardLayers';
import {
  isExternalLayer,
  isLayerItemInCurrentScope,
} from './utils/isLayerItemInCurrentScope';
import {
  itemMatchesWorkspace,
  LayerWorkspaceFilterButton,
} from './LayerWorkspaceFilterButton';

const isFolderItemVisible =
  (
    isInternalLayerVisible: (id: string) => boolean,
    isExternalLayerVisible: (id: string) => boolean
  ) =>
  (item: LayerFolderItemDto) => {
    const { layer } = item;
    return isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);
  };

const areAllFolderItemsVisible = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) => {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  return items.every(checkVisible);
};

const isFolderGroupIndeterminate = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) => {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const someVisible = items.some(checkVisible);
  const allVisible = items.every(checkVisible);
  return someVisible && !allVisible;
};

const handleFolderGroupVisibilityToggle = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean,
  toggleInternalLayerVisibility: (id: string) => void,
  toggleExternalLayerVisibility: (id: string) => void
) => {
  const { items } = group;
  if (items.length === 0) return;

  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const allVisible = items.every(checkVisible);
  const targetVisible = !allVisible;

  items.forEach(({ layer }) => {
    const currentlyVisible = isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);

    if (currentlyVisible !== targetVisible) {
      if (isExternalLayer(layer)) {
        toggleExternalLayerVisibility(layer.id);
      } else {
        toggleInternalLayerVisibility(layer.id);
      }
    }
  });
};

export function ViewLayersPanel({
  onAddLayer,
  onOpenReviewQueue,
}: {
  onAddLayer: OnAddLayer;
  onOpenReviewQueue: () => void;
}) {
  const groupGuid = useGroupGuid();

  const {
    isInternalLayerVisible,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    toggleExternalLayerVisibility,

    incidentFilterId,
    aiLayerPickMode,
  } = useUscgMapContext();

  const deleteLayerNode = useDeleteLayerNodeMutation();
  const isCommandCenter = useIsCommandCenter();
  const tennesseeNationalGuardFlag = useTennesseeNationalGuardFlag();
  const powerOutagesFlag = usePowerOutagesFlag();
  const hubChrome = useHubChromeEnabled();

  const [searchTerm, setSearchTerm] = useState('');
  const [workspaceFilterId, setWorkspaceFilterId] = useState<string | null>(
    null
  );
  const [focusedLayerId, setFocusedLayerId] = useState<string | null>(null);
  const [layerDetailModal, setLayerDetailModal] =
    useState<LayerWithNodeMeta | null>(null);
  const [selectedFolderGroup, setSelectedFolderGroup] =
    useState<LayerGroupDto | null>(null);
  /** Layer row/zoom the FOC Hub COP tour highlights. */
  const [tourRowLayerId, setTourRowLayerId] = useState<string | null>(null);
  /** Layer the FOC Hub COP tour holds expanded. */
  const [tourExpandLayerId, setTourExpandLayerId] = useState<string | null>(
    null
  );

  const { data: layerNodeGroups, isLoading } = useListLayerNodes({
    filter: {
      groupGuid: groupGuid ?? '',
      ...(incidentFilterId ? { incidentIds: [incidentFilterId] } : {}),
      ...(searchTerm && { search: searchTerm }),
    },
  });

  const { standaloneItems, folderGroups } = useMemo(() => {
    const groups = layerNodeGroups?.data ?? [];
    const standalone: LayerFolderItemDto[] = [];
    const folders: LayerGroupDto[] = [];

    for (const group of groups) {
      const scopedItems = group.items.filter(
        (item) =>
          isLayerItemInCurrentScope(item, incidentFilterId) &&
          itemMatchesWorkspace(item.layer, workspaceFilterId)
      );
      if (scopedItems.length === 0) continue;

      if (group.folder == null) {
        standalone.push(...scopedItems);
      } else {
        folders.push({ ...group, items: scopedItems });
      }
    }

    return { standaloneItems: standalone, folderGroups: folders };
  }, [layerNodeGroups?.data, incidentFilterId, workspaceFilterId]);

  const firstTourItem = useMemo((): LayerFolderItemDto | null => {
    if (standaloneItems[0]) return standaloneItems[0];
    for (const folder of folderGroups) {
      if (folder.items[0]) return folder.items[0];
    }
    return null;
  }, [standaloneItems, folderGroups]);

  const firstExternalTourItem = useMemo((): LayerFolderItemDto | null => {
    const external = (item: LayerFolderItemDto) => isExternalLayer(item.layer);
    const fromStandalone = standaloneItems.find(external);
    if (fromStandalone) return fromStandalone;
    for (const folder of folderGroups) {
      const match = folder.items.find(external);
      if (match) return match;
    }
    return firstTourItem;
  }, [standaloneItems, folderGroups, firstTourItem]);

  const firstTourItemRef = useRef(firstTourItem);
  firstTourItemRef.current = firstTourItem;
  const firstExternalTourItemRef = useRef(firstExternalTourItem);
  firstExternalTourItemRef.current = firstExternalTourItem;
  const focusedItem = useMemo(() => {
    if (!focusedLayerId) return null;
    const items = [
      ...standaloneItems,
      ...folderGroups.flatMap((group) => group.items),
    ];
    return items.find((item) => item.layer.id === focusedLayerId) ?? null;
  }, [focusedLayerId, standaloneItems, folderGroups]);

  const tnGuardTimeLayers = useMemo(() => {
    if (!tennesseeNationalGuardFlag) return [] as ExternalLayer[];
    // One control target per AGOL item (hub-scoped after TN Guard ensure).
    const byItemId = new Map<string, ExternalLayer>();
    const consider = (layer: ExternalLayer | InternalLayer) => {
      if (!isExternalLayer(layer)) return;
      if (!getExternalLayerTimeConfig(layer)) return;
      const key = layer.arcgisItemId || layer.id;
      if (!byItemId.has(key)) {
        byItemId.set(key, layer);
      }
    };
    for (const item of standaloneItems) {
      consider(item.layer);
    }
    for (const folder of folderGroups) {
      for (const item of folder.items) {
        consider(item.layer);
      }
    }
    return Array.from(byItemId.values());
  }, [tennesseeNationalGuardFlag, standaloneItems, folderGroups]);

  const toLayerWithNodeMeta = (
    item: LayerFolderItemDto
  ): LayerWithNodeMeta => ({
    ...(isExternalLayer(item.layer)
      ? {
          type: 'external' as const,
          layer: item.layer as ExternalLayer,
        }
      : {
          type: 'internal' as const,
          layer: item.layer as InternalLayer,
        }),
    ...item,
  });

  const toLayerWithNodeMetaRef = useRef(toLayerWithNodeMeta);
  toLayerWithNodeMetaRef.current = toLayerWithNodeMeta;

  useEffect(() => {
    const markFirstRow = () => {
      const item = firstTourItemRef.current;
      setTourRowLayerId(item?.layer.id ?? null);
    };

    const unregisterHub = registerHubCopTourApi({
      showOverview: () => {
        setLayerDetailModal(null);
        setFocusedLayerId(null);
        setTourExpandLayerId(null);
        markFirstRow();
      },
      openFirstLayerDetail: () => {
        const item = firstTourItemRef.current;
        if (!item) {
          setLayerDetailModal(null);
          return;
        }
        setTourRowLayerId(item.layer.id);
        setFocusedLayerId(null);
        setTourExpandLayerId(null);
        setLayerDetailModal(toLayerWithNodeMetaRef.current(item));
      },
      ensureLayerExpanded: () => {
        const item = firstTourItemRef.current;
        if (!item) return;
        setTourRowLayerId(item.layer.id);
        setTourExpandLayerId(item.layer.id);
        setLayerDetailModal(null);
      },
      openSymbology: () => {
        // Symbology lives on the create/edit feature form; expand the first
        // layer so a nearby feature UI is available when present.
        const item = firstTourItemRef.current;
        if (!item) return;
        setTourRowLayerId(item.layer.id);
        setTourExpandLayerId(item.layer.id);
      },
    });

    const showOverview = () => {
      setLayerDetailModal(null);
      setFocusedLayerId(null);
      setTourExpandLayerId(null);
      markFirstRow();
    };
    const openFirstLayerDetail = () => {
      const item = firstTourItemRef.current;
      if (!item) {
        setLayerDetailModal(null);
        return;
      }
      setTourRowLayerId(item.layer.id);
      setFocusedLayerId(null);
      setTourExpandLayerId(null);
      setLayerDetailModal(toLayerWithNodeMetaRef.current(item));
    };
    const expandFirstExternalLayer = () => {
      const item = firstExternalTourItemRef.current;
      if (!item) {
        markFirstRow();
        return;
      }
      setLayerDetailModal(null);
      setFocusedLayerId(null);
      setTourRowLayerId(item.layer.id);
      setTourExpandLayerId(item.layer.id);
    };

    const unregisterFoc = registerFocCopLayersTourApi({
      showOverview,
      openFirstLayerDetail,
      ensureFolderVisible: showOverview,
      expandFirstExternalLayer,
    });

    const unregisterFsltp = registerFocFsltpTourApi({
      showOverview,
      openFirstLayerDetail,
      expandFirstExternalLayer,
    });

    return () => {
      unregisterHub();
      unregisterFoc();
      unregisterFsltp();
    };
  }, []);

  const tourAttrsFor = (layerId: string, external: boolean) =>
    tourRowLayerId === layerId
      ? {
          dataTour: 'hub-cop-layer-row' as const,
          zoomTour: 'hub-cop-layer-zoom' as const,
          ...(external
            ? { qualityTour: 'hub-cop-layer-quality' as const }
            : {}),
        }
      : {};

  const renderLayerItem = (changeArg: LayerWithNodeMeta) => {
    const { layer, type } = changeArg;
    return (
      <LayerListItem
        key={`${type}-${layer.id}-${tourExpandLayerId === layer.id ? 'tour' : 'n'}`}
        item={changeArg}
        defaultExpanded={
          focusedLayerId === layer.id || tourExpandLayerId === layer.id
        }
        onFocusView={
          type === 'internal' ? () => setFocusedLayerId(layer.id) : undefined
        }
        onOpenDetail={
          type === 'external' ? () => setLayerDetailModal(changeArg) : undefined
        }
        tourAttrs={tourAttrsFor(layer.id, type === 'external')}
      />
    );
  };

  const layerModals = (
    <>
      <LayerDetailModal
        key={layerDetailModal?.layer.id ?? 'no-layer'}
        show={layerDetailModal !== null}
        onClose={() => setLayerDetailModal(null)}
        layerWithType={layerDetailModal}
      />
      <ViewLayerFolderModal
        show={selectedFolderGroup !== null}
        onClose={() => setSelectedFolderGroup(null)}
        folderGroup={selectedFolderGroup}
        allGroups={layerNodeGroups?.data ?? []}
      />
    </>
  );

  if (focusedLayerId) {
    const focusedNode = focusedItem ? toLayerWithNodeMeta(focusedItem) : null;
    return (
      <div
        data-tour="hub-layers-workspace"
        className={
          hubChrome
            ? 'tw:flex tw:flex-col tw:gap-3 tw:px-1 tw:pt-1 tw:pb-5 tw:text-foreground'
            : 'd-flex flex-column gap-3 px-2 pt-2 pb-5'
        }
      >
        <MapLayersBackButton onClick={() => setFocusedLayerId(null)} />
        {focusedNode ? (
          hubChrome ? (
            renderLayerItem(focusedNode)
          ) : (
            <ul className="list-unstyled d-grid mb-0 gap-2">
              {renderLayerItem(focusedNode)}
            </ul>
          )
        ) : (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            This layer is no longer in the list.
          </p>
        )}
        {layerModals}
      </div>
    );
  }

  if (hubChrome) {
    const isEmpty =
      !tennesseeNationalGuardFlag &&
      !powerOutagesFlag &&
      standaloneItems.length === 0 &&
      folderGroups.length === 0;

    return (
      <div
        data-tour="hub-layers-workspace"
        className={
          aiLayerPickMode
            ? 'layers-ai-pick-mode tw:flex tw:flex-col tw:gap-3 tw:px-1 tw:pt-1 tw:pb-5 tw:text-foreground'
            : 'tw:flex tw:flex-col tw:gap-3 tw:px-1 tw:pt-1 tw:pb-5 tw:text-foreground'
        }
      >
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <div className="tw:relative tw:min-w-[12rem] tw:flex-1">
            <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search layers…"
              className="tw:h-8 tw:pl-8 tw:text-xs"
              aria-label="Search map layers"
            />
          </div>
          <LayerWorkspaceFilterButton
            value={workspaceFilterId}
            onChange={setWorkspaceFilterId}
          />
          <AddLayerFolderModal variant="shadcn" />
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button type="button" size="sm">
                  + Add Layer
                </Button>
              }
            />
            <DropdownMenuContent align="end" className="dark tw:w-52">
              <DropdownMenuItem
                data-tour="hub-cop-arcgis-add"
                onClick={() => onAddLayer('external')}
              >
                Add from ArcGIS
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAddLayer('internal')}>
                Add Internal Layer
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAddLayer('openWeatherMap')}>
                Add Open Weather Map feed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <ArcGisConnectButton />
          {!isCommandCenter ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-tour="hub-cop-review-queue"
              onClick={onOpenReviewQueue}
            >
              Review queue
            </Button>
          ) : null}
        </div>

        {tennesseeNationalGuardFlag && tnGuardTimeLayers.length > 0 ? (
          <SharedTnGuardLayerTimeControl layers={tnGuardTimeLayers} />
        ) : null}

        <div className="tw:space-y-2">
          {isLoading ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Loading layers…
            </p>
          ) : isEmpty ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              {searchTerm.trim()
                ? 'No layers match this search.'
                : workspaceFilterId
                  ? 'No layers match this workspace.'
                  : 'No layers for this Hub yet.'}
            </p>
          ) : (
            <>
              {tennesseeNationalGuardFlag ? <SitStatTakLayerItem /> : null}
              {powerOutagesFlag ? (
                <>
                  <SitStatPowerOutageLayerItem />
                  <SitStatEriLayerItem />
                </>
              ) : null}
              {standaloneItems.map((item) =>
                renderLayerItem(toLayerWithNodeMeta(item))
              )}
              {folderGroups.map((group, folderIndex) => {
                const folderLabel = group.folder!.name;
                const folderId = group.folder!.id;
                return (
                  <SitStatLayerFolderRow
                    key={`hub-folder-${folderId}`}
                    label={folderLabel}
                    checked={areAllFolderItemsVisible(
                      group,
                      isInternalLayerVisible,
                      isExternalLayerVisible
                    )}
                    indeterminate={isFolderGroupIndeterminate(
                      group,
                      isInternalLayerVisible,
                      isExternalLayerVisible
                    )}
                    onCheckedChange={() =>
                      handleFolderGroupVisibilityToggle(
                        group,
                        isInternalLayerVisible,
                        isExternalLayerVisible,
                        toggleInternalLayerVisibility,
                        toggleExternalLayerVisibility
                      )
                    }
                    defaultOpen={group.items.length > 0}
                    onViewDetails={() => setSelectedFolderGroup(group)}
                    dataTour={
                      folderIndex === 0 ? 'hub-cop-layer-folder' : undefined
                    }
                  >
                    {group.items.length === 0 ? (
                      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                        No layers
                      </p>
                    ) : (
                      group.items.map((item) =>
                        renderLayerItem(toLayerWithNodeMeta(item))
                      )
                    )}
                  </SitStatLayerFolderRow>
                );
              })}
            </>
          )}
        </div>

        {layerModals}
      </div>
    );
  }

  return (
    <div
      data-tour="hub-layers-workspace"
      className={
        aiLayerPickMode
          ? 'd-flex flex-column layers-ai-pick-mode gap-3 px-2 pt-2 pb-5'
          : 'd-flex flex-column gap-3 px-2 pt-2 pb-5'
      }
    >
      <Card>
        <div className="d-flex justify-content-between">
          <StylishNewSearchBar
            placeholder="Search..."
            value={searchTerm}
            onChangeSearchTerm={(val) => setSearchTerm(val.trim())}
          />

          <div className="d-flex gap-2">
            <LayerWorkspaceFilterButton
              value={workspaceFilterId}
              onChange={setWorkspaceFilterId}
            />
            <AddLayerFolderModal />
            <Dropdown style={{ height: '100%' }}>
              <Dropdown.Toggle
                className="button button--primary pe-auto"
                style={{ height: '100%' }}
                id="add-layer-dropdown"
              >
                <SharedIcon iconName="add" size="md" />
                Add Layer
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item
                  data-tour="hub-cop-arcgis-add"
                  onClick={() => {
                    onAddLayer('external');
                  }}
                >
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add from ArcGIS</span>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAddLayer('internal')}>
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add Internal Layer</span>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAddLayer('openWeatherMap')}>
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add Open Weather Map feed</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Card>

      {/* Areas belong to one incident, so the row only means something inside a
          workspace. Every workspace gets it, without anything having to seed it. */}
      {!isCommandCenter ? (
        <ul className="list-unstyled d-grid mb-0 gap-2">
          <IncidentAreaLayerItem />
        </ul>
      ) : null}

      {tennesseeNationalGuardFlag ? (
        <ul className="list-unstyled d-grid mb-0 gap-2">
          <TakLayerItem />
        </ul>
      ) : null}

      {powerOutagesFlag ? (
        <ul className="list-unstyled d-grid mb-0 gap-2">
          <PowerOutageLayerItem />
          <EriLayerItem />
        </ul>
      ) : null}

      {tennesseeNationalGuardFlag && tnGuardTimeLayers.length > 0 ? (
        <SharedTnGuardLayerTimeControl layers={tnGuardTimeLayers} />
      ) : null}

      <div className="d-flex flex-column gap-2">
        <div className="d-flex gap-2">
          <ArcGisConnectButton />
          {!isCommandCenter && (
            <StylishNewButton
              className="button--secondary"
              data-tour="hub-cop-review-queue"
              onClick={onOpenReviewQueue}
            >
              Review queue
            </StylishNewButton>
          )}
        </div>
      </div>

      <div className="d-grid mt-2 gap-2">
        {standaloneItems.length > 0 && (
          <ul className="list-unstyled d-grid mb-0 gap-2">
            {standaloneItems.map((item) =>
              renderLayerItem(toLayerWithNodeMeta(item))
            )}
          </ul>
        )}

        {folderGroups.map((group, folderIndex) => {
          const folderLabel = group.folder!.name;
          const eventKey = group.folder!.id;

          return (
            <Accordion
              key={eventKey}
              defaultActiveKey={group.items.length > 0 ? eventKey : undefined}
            >
              <BSCard
                className="accordion--card border border-neutral-500 bg-neutral-800"
                data-tour={
                  folderIndex === 0 ? 'hub-cop-layer-folder' : undefined
                }
              >
                <AccordionHeader eventKey={eventKey} hasIcon>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewCheckbox
                        className="mt-0"
                        checked={areAllFolderItemsVisible(
                          group,
                          isInternalLayerVisible,
                          isExternalLayerVisible
                        )}
                        indeterminate={isFolderGroupIndeterminate(
                          group,
                          isInternalLayerVisible,
                          isExternalLayerVisible
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderGroupVisibilityToggle(
                            group,
                            isInternalLayerVisible,
                            isExternalLayerVisible,
                            toggleInternalLayerVisibility,
                            toggleExternalLayerVisibility
                          );
                        }}
                      />
                      <span className="fw-semibold">{folderLabel}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewButton
                        className="button--icon bg-bglight-hover border-0 bg-transparent"
                        disabled={group.items.length > 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!group.folder) return;

                          deleteLayerNode.mutate({
                            id: group.folder.id,
                            groupGuid: group.folder.groupGuid,
                          });
                        }}
                        data-testid="delete-layer-folder-btn"
                      >
                        <SharedIcon iconName="delete" size="sm" />
                      </StylishNewButton>
                      <StylishNewButton
                        className="button--icon bg-bglight-hover border-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFolderGroup(group);
                        }}
                      >
                        <SharedIcon iconName="open_in_new" size="sm" />
                      </StylishNewButton>
                    </div>
                  </div>
                </AccordionHeader>
                <Accordion.Collapse eventKey={eventKey}>
                  <BSCard.Body className="p-0">
                    <ul className="list-unstyled d-grid mb-0 gap-2 p-2">
                      {group.items.length === 0 ? (
                        <li className="text-muted small">No layers</li>
                      ) : (
                        group.items.map((item) =>
                          renderLayerItem(toLayerWithNodeMeta(item))
                        )
                      )}
                    </ul>
                  </BSCard.Body>
                </Accordion.Collapse>
              </BSCard>
            </Accordion>
          );
        })}
      </div>

      {layerModals}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-bgdark rounded border border-neutral-500 p-3">
      {children}
    </div>
  );
}
